import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import scenarioLibrary from '../CustomTest/scenarioData';

function AdvancedScenarioDetail() {
  const [scenario, setScenario] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scenarioTags, setScenarioTags] = useState({
    profession: 'General',
    context: 'Office',
    difficulty: 'Intermediate'
  });
  
  const { scenarioId } = useParams();
  const { userData, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 获取场景标签
  const getScenarioTags = (scenario) => {
    // 职业标签
    let profession = "General";
    // 场景标签
    let context = "Office";
    // 难度标签
    let difficulty = "Intermediate";
    
    if (!scenario || !scenario.title_en) return { profession, context, difficulty };
    
    const title = scenario.title_en.toLowerCase();
    const categoryId = scenario.category_id || '';
    
    // 根据标题和类别确定职业标签
    if (title.includes('manager') || title.includes('lead') || title.includes('boss') || title.includes('executive')) {
      profession = "Management";
    } else if (title.includes('client') || title.includes('customer') || title.includes('service')) {
      profession = "Customer Service";
    } else if (title.includes('sales') || title.includes('negotiation') || title.includes('marketing')) {
      profession = "Sales";
    } else if (title.includes('hr') || title.includes('human resource') || title.includes('recruitment')) {
      profession = "HR";
    } else if (title.includes('technical') || title.includes('engineer') || title.includes('developer')) {
      profession = "Technical";
    } else if (title.includes('admin') || title.includes('assistant')) {
      profession = "Administrative";
    }
    
    // 根据类别确定场景标签
    if (categoryId.includes('workplace') || categoryId.includes('office')) {
      context = "Office";
    } else if (categoryId.includes('meeting') || categoryId.includes('presentation')) {
      context = "Meeting";
    } else if (categoryId.includes('social') || categoryId.includes('network')) {
      context = "Social Event";
    } else if (categoryId.includes('interpersonal') || categoryId.includes('relationship')) {
      context = "Relationship";
    } else if (categoryId.includes('conversation') || categoryId.includes('difficult')) {
      context = "Conversation";
    } else if (categoryId.includes('etiquette')) {
      context = "Etiquette";
    }
    
    // 根据内容复杂度确定难度标签
    if (title.includes('basic') || title.includes('simple') || 
        title.includes('introduction') || title.includes('beginning')) {
      difficulty = "Beginner";
    } else if (title.includes('advanced') || title.includes('expert') || 
               title.includes('complex') || title.includes('challenge')) {
      difficulty = "Advanced";
    } else if (title.includes('master') || title.includes('professional') || 
               title.includes('senior') || title.includes('executive')) {
      difficulty = "Expert";
    }
    
    return { profession, context, difficulty };
  };
  
  useEffect(() => {
    async function fetchScenarioDetail() {
      try {
        setLoading(true);
        setError('');
        
        console.log("Fetching scenario with ID:", scenarioId);
        
        // 先检查自定义测试的scenarioLibrary中是否有匹配的场景
        const customScenario = scenarioLibrary.find(s => s.id === scenarioId);
        
        if (customScenario) {
          console.log("Found scenario in custom library:", customScenario);
          
          // 拆分专家建议
          let expertAdvice = customScenario.expertAdvice;
          if (!expertAdvice) {
            // 如果没有专家建议，使用默认文本
            expertAdvice = "Practice this scenario by considering how you would respond to different perspectives and maintaining open communication. Focus on finding common ground while respecting differing viewpoints.";
          }
          
          // 将自定义场景映射到高级场景格式
          setScenario({
            id: customScenario.id,
            title_en: customScenario.title,
            scenario_en: customScenario.description,
            expert_advice_en: expertAdvice,
            category_id: customScenario.tags.context?.[0] || "workplace_social",
            is_premium: false
          });
          
          setCategory({
            id: customScenario.tags.context?.[0] || "workplace_social",
            name_en: customScenario.tags.context?.[0]?.charAt(0).toUpperCase() + customScenario.tags.context?.[0]?.slice(1) || "Workplace"
          });
          
          setLoading(false);
          return;
        }
        
        // Try different paths to load data
        const urls = [
          '/data/combinedScenariosData.json',
          './data/combinedScenariosData.json',
          '/combinedScenariosData.json',
          '../data/combinedScenariosData.json'
        ];
        
        let data = null;
        let successUrl = '';
        
        for (const url of urls) {
          try {
            console.log(`Attempting to load data from ${url}...`);
            const response = await fetch(url);
            
            if (response.ok) {
              data = await response.json();
              successUrl = url;
              console.log(`Successfully loaded scenario details from ${url}`);
              break;
            } else {
              console.log(`Failed to load from ${url}: status ${response.status}`);
            }
          } catch (e) {
            console.error(`Error loading from ${url}:`, e);
          }
        }
        
        if (!data) {
          // 尝试使用硬编码的备用数据路径
          try {
            console.log("Trying fallback data path...");
            const fallbackUrl = window.location.origin + '/combinedScenariosData.json';
            console.log(`Attempting fallback: ${fallbackUrl}`);
            const response = await fetch(fallbackUrl);
            
            if (response.ok) {
              data = await response.json();
              console.log("Successfully loaded scenario details from fallback");
            } else {
              console.log(`Fallback failed with status ${response.status}`);
              throw new Error('Unable to load scenario data. Please check your network connection or refresh the page');
            }
          } catch (e) {
            console.error('Error loading scenario data from all possible sources:', e);
            throw new Error('Unable to load scenario data. Please check your network connection or refresh the page');
          }
        }
        
        console.log(`Data loaded from ${successUrl || 'fallback'}, scenarios count:`, data.scenarios?.length);
        
        // Find scenario with specified ID
        const foundScenario = data.scenarios.find(s => s.id === scenarioId);
        
        if (!foundScenario) {
          console.error(`Scenario with ID ${scenarioId} not found in data. Available IDs:`, 
            data.scenarios.slice(0, 5).map(s => s.id));
          throw new Error(`Scenario with ID ${scenarioId} not found`);
        }
        
        console.log("Found scenario:", foundScenario);
        
        // 所有场景都设为免费，移除premium限制
        // 不再重定向到付款页面
        setScenario({
          ...foundScenario,
          is_premium: false // 强制将所有场景设为免费
        });
        
        // Find corresponding category
        const foundCategory = data.categories.find(c => c.id === foundScenario.category_id);
        setCategory(foundCategory);
        
      } catch (err) {
        console.error('Error loading scenario:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    fetchScenarioDetail();
  }, [scenarioId, userData, isAdmin, navigate, location.state]);
  
  // 设置场景标签
  useEffect(() => {
    if (scenario) {
      const tags = getScenarioTags(scenario);
      setScenarioTags(tags);
    }
  }, [scenario]);
  
  // Get scenario image - using diverse high-quality images to ensure each of 100 premium scenarios has a unique image
  const getScenarioImage = () => {
    if (!scenario) return '/assets/images/scenarios/presentation.jpg';
    
    // First check if this is a custom scenario (from the analysis report unlockable scenarios)
    const customScenario = scenarioLibrary.find(s => s.id === scenario.id);
    if (customScenario && customScenario.imagePlaceholder) {
      return `/assets/images/scenarios/${customScenario.imagePlaceholder}`;
    }
    
    // 使用当前存在的图片文件
    const imageMap = {
      'presentation': '/assets/images/scenarios/presentation.jpg',
      'team-conflict': '/assets/images/scenarios/team-conflict.jpg',
      'networking': '/assets/images/scenarios/networking.jpg',
      'feedback': '/assets/images/scenarios/feedback.jpg',
      'interview': '/assets/images/scenarios/interview.jpg',
      'public-speaking': '/assets/images/scenarios/public-speaking.jpg'
    };
    
    // 根据场景标题选择合适的图片
    if (scenario.title_en) {
      const title = scenario.title_en.toLowerCase();
      
      // 优先通过关键词匹配
      if (title.includes('presentation') || title.includes('speech') || title.includes('public speak')) {
        return imageMap['presentation'];
      } else if (title.includes('team') || title.includes('colleague') || title.includes('conflict')) {
        return imageMap['team-conflict'];
      } else if (title.includes('network') || title.includes('social') || title.includes('party')) {
        return imageMap['networking'];
      } else if (title.includes('feedback') || title.includes('criticism')) {
        return imageMap['feedback'];
      } else if (title.includes('interview') || title.includes('job') || title.includes('hire')) {
        return imageMap['interview'];
      } else if (title.includes('public') || title.includes('audience') || title.includes('speech')) {
        return imageMap['public-speaking'];
      }
    }
    
    // 如果没有匹配，基于ID的哈希选择一张图片
    const imageKeys = Object.keys(imageMap);
      const hash = scenario.id.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
      }, 0);
      
    const randomIndex = Math.abs(hash) % imageKeys.length;
    return imageMap[imageKeys[randomIndex]];
  };
  
  if (loading) {
    return (
      <div className="loading-indicator">
        <div className="spinner"></div>
        <p>Loading scenario details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <h3>Loading Error</h3>
        <p>{error}</p>
        <button className="primary-button" onClick={() => navigate('/advanced-scenarios')}>Back to Scenarios</button>
      </div>
    );
  }
  
  if (!scenario) {
    return (
      <div className="error-container">
        <h3>Scenario Not Found</h3>
        <p>The requested scenario could not be found. Please return to the scenarios list.</p>
        <button className="primary-button" onClick={() => navigate('/advanced-scenarios')}>Back to Scenarios</button>
      </div>
    );
  }
  
  return (
    <div className="scenario-detail-container">
      {/* Back button */}
      <div className="back-link">
        <Link to="/advanced-scenarios">&larr; Back to Scenarios</Link>
      </div>
      
      <h1 className="scenario-title-main">{scenario.title_en}</h1>
      
      {/* 场景标签 */}
      <div className="detail-scenario-tags">
        <span className="profession-tag">{scenarioTags.profession}</span>
        <span className="context-tag">{scenarioTags.context}</span>
        <span className={`difficulty-tag ${scenarioTags.difficulty.toLowerCase()}`}>{scenarioTags.difficulty}</span>
      </div>
      
      {/* Two-column layout - left image, right content */}
      <div className="scenario-detail-layout">
        {/* Left image column */}
        <div className="scenario-detail-image-column">
          <div className="scenario-detail-image-wrapper">
            <img 
              src={getScenarioImage()} 
              alt={scenario.title_en} 
              className="scenario-detail-image" 
              onError={(e) => {
                e.target.src = '/assets/images/scenarios/default.jpg'; 
                // 如果默认图片也加载失败，使用内联占位图
                e.target.onerror = () => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22348%22%20height%3D%22225%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20348%20225%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_189e178ef62%20text%20%7B%20fill%3A%23eceeef%3Bfont-weight%3Abold%3Bfont-family%3AArial%2C%20Helvetica%2C%20Open%20Sans%2C%20sans-serif%2C%20monospace%3Bfont-size%3A17pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_189e178ef62%22%3E%3Crect%20width%3D%22348%22%20height%3D%22225%22%20fill%3D%22%2355595c%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%22116.71875%22%20y%3D%22120.3%22%3EScenario%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
                };
              }}
            />
            {category && (
              <div className="scenario-category-badge">{category.name_en}</div>
            )}
          </div>
        </div>
        
        {/* Right content column */}
        <div className="scenario-detail-content-column">
          <div className="scenario-detail-content">
            <h2>Scenario Description</h2>
            <div className="scenario-description">
              <p>{scenario.scenario_en}</p>
            </div>
            
            <h2>Expert Advice</h2>
            <div className="key-points">
              <p>{scenario.expert_advice_en}</p>
            </div>
            
            <div className="action-section">
              <h3>Ready to improve your social skills?</h3>
              <p>Practice this scenario and many more to build your social intelligence.</p>
              
              <div className="action-buttons">
                <Link to="/advanced-scenarios" className="secondary-button">Explore More Scenarios</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdvancedScenarioDetail; 